import { type FormEvent, useEffect, useRef, useState } from 'react';
import { siteConfig } from '../config/site';
import { submitContact } from '../lib/api';
import { Icon } from './Icon';

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-wavecraft-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Verification failed to load.')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.wavecraftTurnstile = 'true';
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Verification failed to load.')), { once: true });
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

type FormState = 'idle' | 'sending' | 'success' | 'error';

export function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [state, setState] = useState<FormState>('idle');
  const [message, setMessage] = useState('');
  const [setupRequired, setSetupRequired] = useState(false);
  const siteKey = siteConfig.turnstile.siteKey;

  useEffect(() => {
    if (!siteKey || !turnstileRef.current) return;
    let active = true;

    loadTurnstile()
      .then(() => {
        if (!active || !window.turnstile || !turnstileRef.current || widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          action: 'support-contact',
          theme: 'light',
          callback: (token) => setTurnstileToken(token),
          'expired-callback': () => setTurnstileToken(''),
          'error-callback': () => setTurnstileToken(''),
        });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState('error');
        setMessage(error instanceof Error ? error.message : 'Verification failed to load.');
      });

    return () => {
      active = false;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (siteKey && !turnstileToken) {
      setState('error');
      setMessage('Complete the verification before sending your message.');
      return;
    }

    setState('sending');
    setMessage('');
    setSetupRequired(false);

    try {
      const result = await submitContact({
        name: String(data.get('name') ?? ''),
        email: String(data.get('email') ?? ''),
        topic: String(data.get('topic') ?? ''),
        orderNumber: String(data.get('orderNumber') ?? ''),
        message: String(data.get('message') ?? ''),
        website: String(data.get('website') ?? ''),
        turnstileToken,
        page: window.location.href,
      });

      setState(result.ok ? 'success' : 'error');
      setMessage(result.message);
      setSetupRequired(Boolean(result.setupRequired));

      if (result.ok) {
        form.reset();
        setTurnstileToken('');
      }
      if (widgetIdRef.current && window.turnstile) window.turnstile.reset(widgetIdRef.current);
    } catch {
      setState('error');
      setMessage('The support request could not be sent. Please email us directly.');
    }
  };

  return (
    <form className="contact-form" ref={formRef} onSubmit={(event) => void submit(event)}>
      <div className="form-grid">
        <label>
          <span>Name</span>
          <input name="name" type="text" autoComplete="name" minLength={2} maxLength={80} required />
        </label>
        <label>
          <span>Email</span>
          <input name="email" type="email" autoComplete="email" maxLength={254} required />
        </label>
        <label>
          <span>Topic</span>
          <select name="topic" defaultValue="" required>
            <option value="" disabled>Choose one</option>
            <option>Activation or license</option>
            <option>Download or installation</option>
            <option>Compatibility</option>
            <option>Order or refund</option>
            <option>Bug report</option>
            <option>General question</option>
          </select>
        </label>
        <label>
          <span>Order number <small>optional</small></span>
          <input name="orderNumber" type="text" maxLength={100} placeholder="Shown in your receipt" />
        </label>
      </div>
      <label>
        <span>How can we help?</span>
        <textarea name="message" minLength={20} maxLength={5000} rows={7} required placeholder="Include your macOS version, DAW, plugin format and the exact message you see." />
      </label>
      <label className="honeypot" aria-hidden="true">
        Website
        <input name="website" type="text" tabIndex={-1} autoComplete="off" />
      </label>
      {siteKey && <div className="turnstile-slot" ref={turnstileRef} />}
      <div className="contact-form__footer">
        <p>
          Support replies go to the address above. For account recovery, write from the email used for your purchase whenever possible.
        </p>
        <button className="button button--dark" type="submit" disabled={state === 'sending'}>
          {state === 'sending' ? 'Sending…' : 'Send request'}
          <Icon name="arrow" />
        </button>
      </div>
      {state !== 'idle' && message && (
        <div className={`form-message form-message--${state}`} role="status">
          <Icon name={state === 'success' ? 'check' : 'mail'} />
          <div>
            <strong>{state === 'success' ? 'Message sent' : 'Unable to send'}</strong>
            <p>{message}</p>
            {(setupRequired || state === 'error') && (
              <a href={`mailto:${siteConfig.company.supportEmail}`}>Email {siteConfig.company.supportEmail}</a>
            )}
          </div>
        </div>
      )}
    </form>
  );
}
